/**
 * RFI Service
 * Handles Request for Information (RFI) management API calls
 */

import { httpClient, ApiResponse } from './http.client';

export interface RFI {
  rfi_id: string;
  project_id: string;
  rfi_number: string;
  title: string;
  description: string;
  
  // Assignment
  submitted_by: string;
  assigned_to?: string;
  
  // Status & Timing
  status: 'open' | 'pending_response' | 'answered' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  due_date?: string;
  
  // Response
  response?: string;
  responded_by?: string;
  responded_at?: string;
  
  // Related Information
  drawing_references?: string[];
  specification_references?: string[];
  
  // Metadata
  created_at: string;
  updated_at: string;
}

export interface RFICreate {
  organization_id: string;  // Required for new RFIs
  project_id: string;
  rfi_number: string;
  title: string;
  description: string;
  assigned_to?: string;
  status?: 'open' | 'pending_response' | 'answered' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  due_date?: string;
  response?: string;
  drawing_references?: string[];
  specification_references?: string[];
}

export interface RFIUpdate {
  rfi_number?: string;
  title?: string;
  description?: string;
  assigned_to?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  due_date?: string;
  status?: 'open' | 'pending_response' | 'answered' | 'closed';
  response?: string;
  drawing_references?: string[];
  specification_references?: string[];
}

export interface RFIFilters {
  project_id?: string;
  status?: string;
  priority?: string;
  submitted_by?: string;
  assigned_to?: string;
  due_date_from?: string;
  due_date_to?: string;
  search?: string;
}

export interface RFIResponse {
  response: string;
  drawing_attachments?: string[];
  specification_attachments?: string[];
}

class RFIService {
  /**
   * Get all RFIs
   */
  async getRFIs(page = 1, perPage = 20, filters?: RFIFilters): Promise<ApiResponse<{ data: RFI[]; total: number; page: number; per_page: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    });

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    return httpClient.get<{ data: RFI[]; total: number; page: number; per_page: number }>(
      `/api/v1/project-management/rfis?${queryParams}`
    );
  }

  /**
   * Get RFIs for a specific project
   */
  async getProjectRFIs(projectId: string, page = 1, perPage = 50): Promise<ApiResponse<{ data: RFI[]; total: number }>> {
    const queryParams = new URLSearchParams({
      project_id: projectId,
      page: page.toString(),
      per_page: perPage.toString()
    });

    return httpClient.get<{ data: RFI[]; total: number }>(
      `/api/v1/project-management/rfis?${queryParams}`
    );
  }

  /**
   * Get single RFI
   */
  async getRFI(rfiId: string): Promise<ApiResponse<RFI>> {
    return httpClient.get<RFI>(`/api/v1/project-management/rfis/${rfiId}`);
  }

  /**
   * Create new RFI
   */
  async createRFI(rfiData: RFICreate): Promise<ApiResponse<RFI>> {
    return httpClient.post<RFI>('/api/v1/project-management/rfis/', rfiData);
  }

  /**
   * Update RFI
   */
  async updateRFI(rfiId: string, rfiData: RFIUpdate): Promise<ApiResponse<RFI>> {
    return httpClient.put<RFI>(`/api/v1/project-management/rfis/${rfiId}`, rfiData);
  }

  /**
   * Delete RFI
   */
  async deleteRFI(rfiId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/project-management/rfis/${rfiId}`);
  }

  /**
   * Submit RFI response
   */
  async respondToRFI(rfiId: string, responseData: RFIResponse): Promise<ApiResponse<RFI>> {
    // Backend expects response as query parameter
    const queryParams = new URLSearchParams({
      response: responseData.response
    });

    if (responseData.drawing_attachments && responseData.drawing_attachments.length > 0) {
      responseData.drawing_attachments.forEach(ref => {
        queryParams.append('drawing_attachments', ref);
      });
    }

    if (responseData.specification_attachments && responseData.specification_attachments.length > 0) {
      responseData.specification_attachments.forEach(ref => {
        queryParams.append('specification_attachments', ref);
      });
    }

    return httpClient.post<RFI>(
      `/api/v1/project-management/rfis/${rfiId}/respond?${queryParams}`,
      {} // Empty body
    );
  }

  /**
   * Close RFI
   */
  async closeRFI(rfiId: string, closingNotes?: string): Promise<ApiResponse<RFI>> {
    return httpClient.post<RFI>(`/api/v1/rfis/${rfiId}/close`, {
      status: 'closed',
      closing_notes: closingNotes
    });
  }

  /**
   * Reopen RFI
   */
  async reopenRFI(rfiId: string, reason?: string): Promise<ApiResponse<RFI>> {
    return httpClient.post<RFI>(`/api/v1/rfis/${rfiId}/reopen`, {
      status: 'open',
      reopen_reason: reason
    });
  }

  /**
   * Get RFIs by status
   */
  async getRFIsByStatus(status: string, projectId?: string): Promise<ApiResponse<RFI[]>> {
    const queryParams = new URLSearchParams({ status });
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    return httpClient.get<RFI[]>(`/api/v1/rfis/by-status?${queryParams}`);
  }

  /**
   * Get overdue RFIs
   */
  async getOverdueRFIs(projectId?: string): Promise<ApiResponse<RFI[]>> {
    const queryParams = projectId ? `?project_id=${projectId}` : '';
    return httpClient.get<RFI[]>(`/api/v1/rfis/overdue${queryParams}`);
  }

  /**
   * Get RFIs assigned to user
   */
  async getUserRFIs(userId: string, projectId?: string): Promise<ApiResponse<RFI[]>> {
    const queryParams = new URLSearchParams({ assigned_to: userId });
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    return httpClient.get<RFI[]>(`/api/v1/rfis/assigned?${queryParams}`);
  }

  /**
   * Get RFIs submitted by user
   */
  async getRFIsSubmittedByUser(userId: string, projectId?: string): Promise<ApiResponse<RFI[]>> {
    const queryParams = new URLSearchParams({ submitted_by: userId });
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    return httpClient.get<RFI[]>(`/api/v1/rfis/submitted?${queryParams}`);
  }

  /**
   * Bulk update RFI status
   */
  async bulkUpdateRFIStatus(rfiIds: string[], status: string): Promise<ApiResponse<void>> {
    return httpClient.post<void>('/api/v1/rfis/bulk-status-update', {
      rfi_ids: rfiIds,
      status
    });
  }

  /**
   * Bulk assign RFIs
   */
  async bulkAssignRFIs(rfiIds: string[], assignedTo: string): Promise<ApiResponse<void>> {
    return httpClient.post<void>('/api/v1/rfis/bulk-assign', {
      rfi_ids: rfiIds,
      assigned_to: assignedTo
    });
  }

  /**
   * Search RFIs
   */
  async searchRFIs(query: string, projectId?: string): Promise<ApiResponse<RFI[]>> {
    const queryParams = new URLSearchParams({ q: query });
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    return httpClient.get<RFI[]>(`/api/v1/rfis/search?${queryParams}`);
  }

  /**
   * Get next RFI number for project
   */
  async getNextRFINumber(projectId: string): Promise<ApiResponse<{ rfi_number: string }>> {
    return httpClient.get<{ rfi_number: string }>(`/api/v1/projects/${projectId}/next-rfi-number`);
  }

  /**
   * Add drawing reference to RFI
   */
  async addDrawingReference(rfiId: string, drawingReference: string): Promise<ApiResponse<RFI>> {
    return httpClient.post<RFI>(`/api/v1/rfis/${rfiId}/drawing-references`, {
      drawing_reference: drawingReference
    });
  }

  /**
   * Remove drawing reference from RFI
   */
  async removeDrawingReference(rfiId: string, drawingReference: string): Promise<ApiResponse<RFI>> {
    return httpClient.delete<RFI>(`/api/v1/rfis/${rfiId}/drawing-references/${encodeURIComponent(drawingReference)}`);
  }

  /**
   * Add specification reference to RFI
   */
  async addSpecificationReference(rfiId: string, specificationReference: string): Promise<ApiResponse<RFI>> {
    return httpClient.post<RFI>(`/api/v1/rfis/${rfiId}/specification-references`, {
      specification_reference: specificationReference
    });
  }

  /**
   * Remove specification reference from RFI
   */
  async removeSpecificationReference(rfiId: string, specificationReference: string): Promise<ApiResponse<RFI>> {
    return httpClient.delete<RFI>(`/api/v1/rfis/${rfiId}/specification-references/${encodeURIComponent(specificationReference)}`);
  }

  /**
   * Get RFI analytics
   */
  async getRFIAnalytics(projectId: string, period: 'week' | 'month' | 'quarter' = 'month'): Promise<ApiResponse<{
    total_rfis: number;
    open_rfis: number;
    answered_rfis: number;
    closed_rfis: number;
    overdue_rfis: number;
    average_response_time_days: number;
    rfis_by_priority: { [key: string]: number };
    response_rate: number;
  }>> {
    return httpClient.get<{
      total_rfis: number;
      open_rfis: number;
      answered_rfis: number;
      closed_rfis: number;
      overdue_rfis: number;
      average_response_time_days: number;
      rfis_by_priority: { [key: string]: number };
      response_rate: number;
    }>(`/api/v1/rfis/analytics?project_id=${projectId}&period=${period}`);
  }

  /**
   * Generate RFI report
   */
  async generateRFIReport(projectId: string, status?: string, format: 'pdf' | 'excel' = 'pdf'): Promise<ApiResponse<{ download_url: string }>> {
    return httpClient.post<{ download_url: string }>('/api/v1/rfis/report', {
      project_id: projectId,
      status,
      format
    });
  }

  /**
   * Get RFI activity log
   */
  async getRFIActivity(rfiId: string): Promise<ApiResponse<Array<{
    action: string;
    user_id: string;
    user_name: string;
    timestamp: string;
    details?: string;
  }>>> {
    return httpClient.get<Array<{
      action: string;
      user_id: string;
      user_name: string;
      timestamp: string;
      details?: string;
    }>>(`/api/v1/rfis/${rfiId}/activity`);
  }

  /**
   * Get RFI notifications/reminders
   */
  async getRFINotifications(userId: string): Promise<ApiResponse<Array<{
    rfi_id: string;
    rfi_number: string;
    title: string;
    notification_type: 'overdue' | 'due_soon' | 'assignment' | 'response';
    days_overdue?: number;
    due_in_days?: number;
  }>>> {
    return httpClient.get<Array<{
      rfi_id: string;
      rfi_number: string;
      title: string;
      notification_type: 'overdue' | 'due_soon' | 'assignment' | 'response';
      days_overdue?: number;
      due_in_days?: number;
    }>>(`/api/v1/rfis/notifications?user_id=${userId}`);
  }

  /**
   * Export RFI to various formats
   */
  async exportRFI(rfiId: string, format: 'pdf' | 'word'): Promise<ApiResponse<{ download_url: string }>> {
    return httpClient.post<{ download_url: string }>(`/api/v1/rfis/${rfiId}/export`, {
      format
    });
  }

  /**
   * Duplicate RFI
   */
  async duplicateRFI(rfiId: string, newTitle?: string): Promise<ApiResponse<RFI>> {
    return httpClient.post<RFI>(`/api/v1/rfis/${rfiId}/duplicate`, {
      new_title: newTitle
    });
  }

  /**
   * Set RFI reminder
   */
  async setRFIReminder(rfiId: string, reminderDate: string): Promise<ApiResponse<void>> {
    return httpClient.post<void>(`/api/v1/rfis/${rfiId}/reminder`, {
      reminder_date: reminderDate
    });
  }
}

// Export singleton instance
export const rfiService = new RFIService();